// DocumentInfoPanelManager
// Manages the document info panel: fields, autocomplete, AI actions row, review stats

import { api, type Document } from '../services/api';
import { storage } from '../services/storage';
import { FIELD_CONFIG } from '../config/field-config';
import type { AIActionsController } from './AIActionsController';
import type { TranscriptionController } from './TranscriptionController';
import type { Sidebar } from './Sidebar';
import type { TabManager } from './TabManager';

export interface DocInfoPanelCallbacks {
  getCurrentDoc: () => Document | null;
  setCurrentDoc: (doc: Document) => void;
  getActiveTabId: () => string | null;
  addTag: (tag: string) => Promise<void>;
  removeTag: (tag: string) => Promise<void>;
  onDelete: (docId: string) => Promise<void>;
  toggleKnowledge: (docId: string, enabled: boolean) => Promise<void>;
}

export class DocumentInfoPanelManager {
  private docInfoFields: Set<string> = new Set(['summary']);
  private docInfoPanelVisibleDocs: Set<string> = new Set();
  private docInfoPanelEl: HTMLElement | null = null;
  private openedFromKnowledgeReview: boolean = false;

  private callbacks: DocInfoPanelCallbacks;
  private aiActionsController: AIActionsController;
  private transcriptionController: TranscriptionController;
  private sidebar: Sidebar;
  private tabManager: TabManager;

  constructor(
    callbacks: DocInfoPanelCallbacks,
    aiActionsController: AIActionsController,
    transcriptionController: TranscriptionController,
    sidebar: Sidebar,
    tabManager: TabManager
  ) {
    this.callbacks = callbacks;
    this.aiActionsController = aiActionsController;
    this.transcriptionController = transcriptionController;
    this.sidebar = sidebar;
    this.tabManager = tabManager;
    this.docInfoPanelEl = document.getElementById('doc-info-panel');
  }

  setOpenedFromReview(value: boolean): void {
    this.openedFromKnowledgeReview = value;
  }

  isVisibleForDoc(docId: string): boolean {
    return this.docInfoPanelVisibleDocs.has(docId);
  }

  addVisibleDoc(docId: string): void {
    this.docInfoPanelVisibleDocs.add(docId);
  }

  showPanel(): void {
    this.docInfoPanelEl?.classList.remove('hidden');
  }

  hidePanel(): void {
    this.docInfoPanelEl?.classList.add('hidden');
  }

  toggle(docId: string): void {
    if (!this.docInfoPanelEl) return;

    const isCurrentlyVisible = this.docInfoPanelVisibleDocs.has(docId);

    if (isCurrentlyVisible) {
      this.docInfoPanelVisibleDocs.delete(docId);
      this.docInfoPanelEl.classList.add('hidden');
    } else {
      this.docInfoPanelVisibleDocs.add(docId);
      this.docInfoPanelEl.classList.remove('hidden');
      this.loadVisibleFields();
      this.setup();
      this.renderFields();
    }
  }

  updateForDoc(docId: string): void {
    if (!this.docInfoPanelEl) return;

    const currentDoc = this.callbacks.getCurrentDoc();
    if (!currentDoc || currentDoc.meta.id !== docId) {
      this.docInfoPanelEl.classList.add('hidden');
      return;
    }

    const customFields = currentDoc.meta.customFields as Record<string, string> | undefined;
    const hasTranscription = customFields?.transcriptionStatus === 'completed' || !!customFields?.transcription;
    if (hasTranscription && !this.docInfoPanelVisibleDocs.has(docId)) {
      this.docInfoPanelVisibleDocs.add(docId);
    }

    const isFromReview = this.openedFromKnowledgeReview;
    if (isFromReview) {
      this.docInfoPanelVisibleDocs.add(docId);
      this.openedFromKnowledgeReview = false;
    }

    const shouldBeVisible = this.docInfoPanelVisibleDocs.has(docId);

    if (shouldBeVisible) {
      this.docInfoPanelEl.classList.remove('hidden');

      if (isFromReview) {
        this.docInfoFields = new Set([
          'summary', 'outline', 'url', 'author',
          'project', 'area', 'tags',
          'resourceType', 'contentFormat', 'platform',
          'intent', 'status'
        ]);
      } else {
        this.loadVisibleFields();
      }

      this.setup();
      this.renderFields();
    } else {
      this.docInfoPanelEl.classList.add('hidden');
    }
  }

  setup(): void {
    const addFieldBtn = document.getElementById('btn-add-field');
    const fieldMenu = document.getElementById('field-menu');

    if (addFieldBtn && fieldMenu) {
      addFieldBtn.onclick = () => {
        fieldMenu.classList.toggle('hidden');
      };

      fieldMenu.querySelectorAll('button').forEach(btn => {
        btn.onclick = async () => {
          const field = btn.getAttribute('data-field');
          if (field && !this.docInfoFields.has(field)) {
            this.docInfoFields.add(field);
            await this.saveVisibleFields();
            this.renderFields();
          }
          fieldMenu.classList.add('hidden');
        };
      });

      document.addEventListener('click', (e) => {
        if (!addFieldBtn.contains(e.target as Node) && !fieldMenu.contains(e.target as Node)) {
          fieldMenu.classList.add('hidden');
        }
      });
    }

    const currentDoc = this.callbacks.getCurrentDoc();
    const knowledgeBtn = document.getElementById('knowledge-toggle');
    if (knowledgeBtn && currentDoc) {
      knowledgeBtn.classList.toggle('active', currentDoc.meta.isKnowledgeEnabled === true);

      knowledgeBtn.onclick = async () => {
        const doc = this.callbacks.getCurrentDoc();
        if (doc) {
          const newState = !doc.meta.isKnowledgeEnabled;
          await this.callbacks.toggleKnowledge(doc.meta.id, newState);
          knowledgeBtn.classList.toggle('active', newState);
        }
      };
    }

    const deleteBtn = document.getElementById('doc-delete-btn');
    if (deleteBtn) {
      deleteBtn.onclick = async () => {
        const doc = this.callbacks.getCurrentDoc();
        if (doc) {
          const docId = doc.meta.id;
          const title = doc.meta.title;
          const confirmed = confirm(`Delete "${title}"?\n\nThis will permanently delete the document and all associated data.`);
          if (!confirmed) return;
          await this.callbacks.onDelete(docId);
        }
      };
    }
  }

  renderFields(): void {
    const container = document.getElementById('doc-info-fields');
    const currentDoc = this.callbacks.getCurrentDoc();
    if (!container || !currentDoc) return;

    const activeTabId = this.callbacks.getActiveTabId();
    if (activeTabId && currentDoc.meta.id !== activeTabId) {
      return;
    }

    container.innerHTML = '';

    const customFields = this.getCustomFields();
    const meta = { ...currentDoc.meta, ...customFields } as any;

    const footerFields = ['project', 'area', 'tags'];
    const regularFields: string[] = [];
    const compactFields: string[] = [];
    const footerLeftFields: string[] = [];
    const footerRightFields: string[] = [];

    this.docInfoFields.forEach(fieldType => {
      if (footerFields.includes(fieldType)) {
        if (fieldType === 'tags') {
          footerRightFields.push(fieldType);
        } else {
          footerLeftFields.push(fieldType);
        }
      } else {
        const config = FIELD_CONFIG[fieldType];
        if (config?.layout === 'compact') {
          compactFields.push(fieldType);
        } else {
          regularFields.push(fieldType);
        }
      }
    });

    // Review stats row
    const reviewStatsRow = this.createReviewStatsRow();
    if (reviewStatsRow) {
      container.appendChild(reviewStatsRow);
    }

    // Compact fields row
    if (compactFields.length > 0) {
      const row = document.createElement('div');
      row.className = 'doc-info-fields-row';

      const orderedCompact = [...compactFields].sort((a, b) => {
        const order = ['platform', 'resourceCategory', 'contentFormat', 'intent', 'status', 'date'];
        return order.indexOf(a) - order.indexOf(b);
      });

      orderedCompact.forEach(fieldType => {
        const fieldEl = this.createInfoField(fieldType, meta);
        if (fieldEl) row.appendChild(fieldEl);

        if (fieldType === 'platform') {
          const transcribeBtn = this.transcriptionController.createButton();
          row.appendChild(transcribeBtn);
        }
      });

      if (!orderedCompact.includes('platform') && this.transcriptionController.hasVideoUrl()) {
        const transcribeBtn = this.transcriptionController.createButton();
        row.insertBefore(transcribeBtn, row.firstChild);
      }

      container.appendChild(row);
    } else if (this.transcriptionController.hasVideoUrl()) {
      const row = document.createElement('div');
      row.className = 'doc-info-fields-row';
      row.appendChild(this.transcriptionController.createButton());
      container.appendChild(row);
    }

    // Footer row (Project/Area left, Tags right)
    if (footerLeftFields.length > 0 || footerRightFields.length > 0) {
      const footerRow = document.createElement('div');
      footerRow.className = 'doc-info-footer-row';

      if (footerLeftFields.length > 0) {
        const leftDiv = document.createElement('div');
        leftDiv.className = 'doc-info-footer-left';
        footerLeftFields.forEach(fieldType => {
          const fieldEl = this.createInfoField(fieldType, meta);
          if (fieldEl) leftDiv.appendChild(fieldEl);
        });
        footerRow.appendChild(leftDiv);
      } else {
        footerRow.style.justifyContent = 'flex-start';
      }

      if (footerRightFields.length > 0) {
        const rightDiv = document.createElement('div');
        rightDiv.className = 'doc-info-footer-right';
        footerRightFields.forEach(fieldType => {
          const fieldEl = this.createInfoField(fieldType, meta);
          if (fieldEl) rightDiv.appendChild(fieldEl);
        });
        footerRow.appendChild(rightDiv);
      }

      container.appendChild(footerRow);
    }

    // Full-width fields
    const orderedRegular = [...regularFields].sort((a, b) => {
      const order = ['url', 'author', 'speakers', 'summary', 'outline'];
      return order.indexOf(a) - order.indexOf(b);
    });
    orderedRegular.forEach(fieldType => {
      const fieldEl = this.createInfoField(fieldType, meta);
      if (fieldEl) container.appendChild(fieldEl);
    });

    // Update menu to disable already-added fields
    const fieldMenu = document.getElementById('field-menu');
    if (fieldMenu) {
      fieldMenu.querySelectorAll('button[data-field]').forEach(btn => {
        const field = btn.getAttribute('data-field');
        if (field) {
          (btn as HTMLButtonElement).disabled = this.docInfoFields.has(field);
        }
      });
    }
  }

  private createReviewStatsRow(): HTMLElement | null {
    const currentDoc = this.callbacks.getCurrentDoc();
    if (!currentDoc) return null;

    const { humanReview, completionScore } = currentDoc.meta;

    if (humanReview === undefined && completionScore === undefined) {
      return null;
    }

    const row = document.createElement('div');
    row.className = 'review-stats-row';

    if (completionScore !== undefined) {
      const score = Math.max(0, Math.min(100, completionScore));
      const scoreColor = score >= 80 ? '#10b981' : score >= 50 ? '#f59e0b' : '#ef4444';

      const scoreDiv = document.createElement('div');
      scoreDiv.className = 'review-score';
      scoreDiv.innerHTML = `
        <div class="review-score-header">
          <span class="material-symbols-outlined">analytics</span>
          <span>Completion</span>
          <span class="review-score-value">${score}%</span>
        </div>
        <div class="review-score-bar">
          <div class="review-score-fill" style="width: ${score}%; background: ${scoreColor}"></div>
        </div>
      `;
      row.appendChild(scoreDiv);
    }

    const reviewDiv = document.createElement('div');
    reviewDiv.className = 'review-toggle';

    const isReviewed = humanReview === true;

    reviewDiv.innerHTML = `
      <label class="review-toggle-label">
        <input type="checkbox" class="review-checkbox" ${isReviewed ? 'checked' : ''}>
        <span class="material-symbols-outlined">rate_review</span>
        <span>Reviewed</span>
      </label>
    `;

    const checkbox = reviewDiv.querySelector('.review-checkbox') as HTMLInputElement;
    checkbox?.addEventListener('change', async () => {
      const doc = this.callbacks.getCurrentDoc();
      if (!doc) return;

      const newValue = checkbox.checked;
      const response = await api.updateDocument(doc.meta.id, { humanReview: newValue });

      if (response.success) {
        doc.meta.humanReview = newValue;
        console.log(`✓ Human review set to: ${newValue}`);
      } else {
        checkbox.checked = !newValue;
        alert('Failed to update review status');
      }
    });

    row.appendChild(reviewDiv);

    return row;
  }

  private createInfoField(fieldType: string, meta: any): HTMLElement | null {
    const config = FIELD_CONFIG[fieldType];
    if (!config) return null;

    const field = document.createElement('div');
    field.className = 'doc-info-field';
    field.dataset.field = fieldType;

    const label = document.createElement('span');
    label.className = 'field-label';
    label.textContent = this.getFieldLabel(fieldType);
    field.appendChild(label);

    switch (config.type) {
      case 'textarea': {
        const textarea = document.createElement('textarea');
        textarea.className = 'field-value multiline';
        textarea.value = meta[fieldType] || '';
        textarea.placeholder = `Add ${this.getFieldLabel(fieldType).toLowerCase()}...`;

        let saveTimeout: ReturnType<typeof setTimeout>;
        textarea.oninput = () => {
          clearTimeout(saveTimeout);
          textarea.style.borderColor = '#fbbf24';
          saveTimeout = setTimeout(() => {
            this.saveFieldValue(fieldType, textarea.value, true);
            textarea.style.borderColor = '#22c55e';
            setTimeout(() => { textarea.style.borderColor = ''; }, 500);
          }, 500);
        };
        textarea.onblur = () => {
          clearTimeout(saveTimeout);
          this.saveFieldValue(fieldType, textarea.value, true);
        };
        field.appendChild(textarea);
        break;
      }
      case 'tags': {
        const tagsContainer = document.createElement('div');
        tagsContainer.className = 'field-tags';

        const tags = meta.tags || [];
        tags.forEach((tag: string) => {
          const tagEl = this.createInfoTagElement(tag);
          tagsContainer.appendChild(tagEl);
        });

        const input = document.createElement('input');
        input.type = 'text';
        input.className = 'tag-input';
        input.placeholder = '+ add';
        input.onkeydown = (e) => {
          if (e.key === 'Enter' && input.value.trim()) {
            e.preventDefault();
            this.addInfoTag(input.value.trim());
            input.value = '';
          }
        };
        tagsContainer.appendChild(input);
        field.appendChild(tagsContainer);
        break;
      }
      case 'select': {
        const wrapper = document.createElement('div');
        wrapper.className = 'field-combobox';

        const input = document.createElement('input');
        input.type = 'text';
        input.className = 'field-compact';
        input.value = meta[fieldType] || '';
        input.placeholder = 'Select or type...';

        const dropdown = document.createElement('div');
        dropdown.className = 'combobox-dropdown hidden';

        const savedOptions = this.getCustomSelectOptions(fieldType);
        const allOptions = [...new Set([...(config.options || []), ...savedOptions])];

        const renderDropdownOptions = (filter: string = '') => {
          dropdown.innerHTML = '';
          const query = filter.toLowerCase();
          const filtered = allOptions.filter(opt => opt.toLowerCase().includes(query));

          filtered.forEach(opt => {
            const item = document.createElement('div');
            item.className = 'combobox-item';
            item.textContent = opt;
            item.onclick = () => {
              input.value = opt;
              this.saveFieldValue(fieldType, opt);
              dropdown.classList.add('hidden');
            };
            dropdown.appendChild(item);
          });

          if (filter && !allOptions.some(opt => opt.toLowerCase() === query)) {
            const createItem = document.createElement('div');
            createItem.className = 'combobox-item create-new';
            createItem.innerHTML = `<span class="material-symbols-outlined">add</span> Create "${filter}"`;
            createItem.onclick = () => {
              this.addCustomSelectOption(fieldType, filter);
              input.value = filter;
              this.saveFieldValue(fieldType, filter);
              dropdown.classList.add('hidden');
              input.style.background = '#dcfce7';
              setTimeout(() => { input.style.background = ''; }, 500);
            };
            dropdown.appendChild(createItem);
          }
        };

        input.onfocus = () => {
          dropdown.classList.remove('hidden');
          renderDropdownOptions(input.value);
        };
        input.oninput = () => renderDropdownOptions(input.value);
        input.onblur = () => setTimeout(() => dropdown.classList.add('hidden'), 150);
        input.onkeydown = (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            const val = input.value.trim();
            if (val && !allOptions.includes(val)) {
              this.addCustomSelectOption(fieldType, val);
            }
            this.saveFieldValue(fieldType, val);
            dropdown.classList.add('hidden');
          }
        };

        wrapper.appendChild(input);
        wrapper.appendChild(dropdown);
        field.appendChild(wrapper);
        break;
      }
      case 'autocomplete': {
        const wrapper = document.createElement('div');
        wrapper.className = 'field-autocomplete';

        const input = document.createElement('input');
        input.type = 'text';
        input.className = 'field-compact';

        let displayValue = meta[fieldType] || '';
        if (fieldType === 'project' && meta.folderId) {
          const folder = this.sidebar.getFolders().find(f => f.id === meta.folderId);
          if (folder) displayValue = folder.name;
        } else if (fieldType === 'area' && meta.workspaceId) {
          const workspace = this.sidebar.getWorkspaces().find(w => w.id === meta.workspaceId);
          if (workspace) displayValue = workspace.name;
        }
        input.value = displayValue;
        input.placeholder = `Search or create...`;

        const dropdown = document.createElement('div');
        dropdown.className = 'autocomplete-dropdown hidden';

        input.onfocus = () => this.showAutocomplete(fieldType, input, dropdown);
        input.oninput = () => this.filterAutocomplete(fieldType, input, dropdown);
        input.onblur = () => setTimeout(() => dropdown.classList.add('hidden'), 150);
        input.onkeydown = (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            this.selectOrCreateAutocomplete(fieldType, input.value.trim(), input, dropdown);
          }
        };

        wrapper.appendChild(input);
        wrapper.appendChild(dropdown);
        field.appendChild(wrapper);
        break;
      }
      case 'readonly': {
        const span = document.createElement('span');
        span.className = 'field-readonly';
        if (fieldType === 'date') {
          const currentDoc = this.callbacks.getCurrentDoc();
          const timestamp = currentDoc?.meta.created_at;
          if (timestamp) {
            const date = new Date(timestamp);
            const dd = String(date.getDate()).padStart(2, '0');
            const mm = String(date.getMonth() + 1).padStart(2, '0');
            const yyyy = date.getFullYear();
            const hh = String(date.getHours()).padStart(2, '0');
            const min = String(date.getMinutes()).padStart(2, '0');
            span.textContent = `${dd}/${mm}/${yyyy} ${hh}:${min}`;
          } else {
            span.textContent = '-';
          }
        } else {
          span.textContent = meta[fieldType] || '-';
        }
        field.appendChild(span);
        break;
      }
      case 'text':
      default: {
        if (fieldType === 'url') {
          const urlWrapper = document.createElement('div');
          urlWrapper.className = 'field-url-wrapper';

          const openBtn = document.createElement('button');
          openBtn.className = 'field-url-open';
          openBtn.innerHTML = '<span class="material-symbols-outlined">open_in_new</span>';
          openBtn.title = 'Open URL';
          openBtn.onclick = () => {
            const url = meta[fieldType];
            if (url) window.open(url, '_blank', 'noopener,noreferrer');
          };
          urlWrapper.appendChild(openBtn);

          const input = document.createElement('input');
          input.type = 'text';
          input.className = 'field-value';
          input.value = meta[fieldType] || '';
          input.placeholder = 'Add url...';
          input.onblur = () => this.saveFieldValue(fieldType, input.value);
          urlWrapper.appendChild(input);

          field.appendChild(urlWrapper);
        } else {
          const input = document.createElement('input');
          input.type = 'text';
          input.className = config.layout === 'compact' ? 'field-compact' : 'field-value';
          input.value = meta[fieldType] || '';
          input.placeholder = `Add ${this.getFieldLabel(fieldType).toLowerCase()}...`;
          input.onblur = () => this.saveFieldValue(fieldType, input.value);
          field.appendChild(input);
        }
        break;
      }
    }

    // Remove button
    const removeBtn = document.createElement('button');
    removeBtn.className = 'field-remove';
    removeBtn.innerHTML = '<span class="material-symbols-outlined">close</span>';
    removeBtn.onclick = async () => {
      this.docInfoFields.delete(fieldType);
      await this.saveVisibleFields();
      this.renderFields();
    };
    field.appendChild(removeBtn);

    return field;
  }

  private getFieldLabel(fieldType: string): string {
    const labels: Record<string, string> = {
      summary: 'Summary',
      outline: 'Outline',
      speakers: 'Speakers',
      tags: 'Tags',
      url: 'URL',
      project: 'Project',
      area: 'Area',
      author: 'Author',
      resourceCategory: 'Category',
      contentFormat: 'Format',
      platform: 'Platform',
      intent: 'Intent',
      status: 'Status',
      date: 'Date'
    };
    return labels[fieldType] || fieldType;
  }

  private async showAutocomplete(fieldType: string, input: HTMLInputElement, dropdown: HTMLElement): Promise<void> {
    dropdown.classList.remove('hidden');
    await this.filterAutocomplete(fieldType, input, dropdown);
  }

  private async filterAutocomplete(fieldType: string, input: HTMLInputElement, dropdown: HTMLElement): Promise<void> {
    const query = input.value.toLowerCase().trim();

    let items: Array<{ id: string; name: string; color?: string }> = [];

    if (fieldType === 'project') {
      const folders = this.sidebar.getFolders().filter(f => f.name.toLowerCase().includes(query));
      const seen = new Set<string>();
      items = folders.filter(f => {
        if (seen.has(f.id)) return false;
        seen.add(f.id);
        return true;
      }).map(f => ({ id: f.id, name: f.name, color: '#93c5fd' }));
    } else if (fieldType === 'area') {
      const workspaces = this.sidebar.getWorkspaces().filter(w => w.name.toLowerCase().includes(query));
      const seen = new Set<string>();
      items = workspaces.filter(w => {
        if (seen.has(w.id)) return false;
        seen.add(w.id);
        return true;
      }).map(w => ({ id: w.id, name: w.name, color: w.color }));
    }

    dropdown.innerHTML = '';

    items.slice(0, 5).forEach(item => {
      const itemEl = document.createElement('div');
      itemEl.className = 'autocomplete-item';
      itemEl.innerHTML = `
        <span class="item-color" style="background: ${item.color || '#9ca3af'}"></span>
        <span>${item.name}</span>
      `;
      itemEl.onclick = () => {
        if (fieldType === 'project' || fieldType === 'area') {
          this.selectOrCreateAutocomplete(fieldType, item.name, input, dropdown);
        } else {
          input.value = item.name;
          this.saveFieldValue(fieldType, item.name);
          dropdown.classList.add('hidden');
        }
      };
      dropdown.appendChild(itemEl);
    });

    if (query) {
      const createEl = document.createElement('div');
      createEl.className = 'autocomplete-item create-new';
      createEl.innerHTML = `
        <span class="material-symbols-outlined">add</span>
        <span>Create "${query}"</span>
      `;
      createEl.onclick = () => {
        this.selectOrCreateAutocomplete(fieldType, query, input, dropdown);
      };
      dropdown.appendChild(createEl);
    }
  }

  private async selectOrCreateAutocomplete(fieldType: string, value: string, input: HTMLInputElement, dropdown: HTMLElement): Promise<void> {
    if (!value) return;
    const currentDoc = this.callbacks.getCurrentDoc();
    if (!currentDoc) return;

    let created = false;
    let folderId: string | null = null;
    let workspaceId: string | null = null;

    if (fieldType === 'project') {
      let folder = this.sidebar.getFolders().find(f => f.name.toLowerCase() === value.toLowerCase());

      if (!folder) {
        const response = await api.createFolder(value, null);
        if (response.success && response.data) {
          folder = response.data;
          this.sidebar.addFolder(response.data);
          created = true;
        } else {
          console.error('Failed to create folder:', response.error);
          input.style.background = '#fecaca';
          setTimeout(() => { input.style.background = ''; }, 1000);
          return;
        }
      }

      folderId = folder.id;
    } else if (fieldType === 'area') {
      let workspace = this.sidebar.getWorkspaces().find(w => w.name.toLowerCase() === value.toLowerCase());

      if (!workspace) {
        const response = await api.createWorkspace(value, '#86efac');
        if (response.success && response.data) {
          workspace = response.data;
          await this.sidebar.loadWorkspaces();
          created = true;
        } else {
          console.error('Failed to create workspace:', response.error);
          input.style.background = '#fecaca';
          setTimeout(() => { input.style.background = ''; }, 1000);
          return;
        }
      }

      workspaceId = workspace.id;
    }

    input.value = value;

    if (created) {
      input.style.background = '#dcfce7';
      input.style.borderColor = '#22c55e';
      setTimeout(() => {
        input.style.background = '';
        input.style.borderColor = '';
      }, 1000);
    }

    if (folderId) {
      const response = await api.updateDocument(currentDoc.meta.id, { folderId });
      if (response.success && response.data) {
        this.callbacks.setCurrentDoc(response.data);
        storage.setLocalVersion(currentDoc.meta.id, response.data.meta.version);
      }
      await this.sidebar.loadDocuments();
      await this.sidebar.loadFolders();
    }
    if (workspaceId) {
      const response = await api.updateDocument(currentDoc.meta.id, { workspaceId });
      if (response.success && response.data) {
        this.callbacks.setCurrentDoc(response.data);
        storage.setLocalVersion(currentDoc.meta.id, response.data.meta.version);
      }
      await this.sidebar.loadDocuments();
    }

    this.renderFields();
    dropdown.classList.add('hidden');
  }

  private createInfoTagElement(tag: string): HTMLElement {
    const tagEl = document.createElement('span');
    tagEl.className = 'field-tag';
    tagEl.innerHTML = `#${tag}<span class="remove-tag">×</span>`;
    tagEl.querySelector('.remove-tag')?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.callbacks.removeTag(tag);
      this.renderFields();
    });
    return tagEl;
  }

  private async addInfoTag(tag: string): Promise<void> {
    await this.callbacks.addTag(tag);
    this.renderFields();
  }

  async saveFieldValue(field: string, value: any, skipRender: boolean = false): Promise<void> {
    const currentDoc = this.callbacks.getCurrentDoc();
    if (!currentDoc) return;

    try {
      const coreFields = ['tags', 'title', 'isFavorite', 'folderId', 'isArchived', 'workspaceId', 'resourceCategory', 'project', 'type'];

      if (coreFields.includes(field)) {
        const response = await api.updateDocument(currentDoc.meta.id, { [field]: value });
        if (response.success && response.data) {
          this.callbacks.setCurrentDoc(response.data);
          storage.setLocalVersion(currentDoc.meta.id, response.data.meta.version);
          await this.sidebar.loadDocuments();
        }
      } else {
        const customFields = { ...(currentDoc.meta.customFields || {}) };
        customFields[field] = value;

        const response = await api.updateDocument(currentDoc.meta.id, { customFields });
        if (response.success && response.data) {
          this.callbacks.setCurrentDoc(response.data);
          storage.setLocalVersion(currentDoc.meta.id, response.data.meta.version);
        }
      }

      if (!skipRender) {
        this.renderFields();
      }
    } catch (err) {
      console.error('Failed to save field:', err);
    }
  }

  private getCustomFields(): Record<string, unknown> {
    const currentDoc = this.callbacks.getCurrentDoc();
    if (!currentDoc) return {};
    return currentDoc.meta.customFields || {};
  }

  private async saveVisibleFields(): Promise<void> {
    const currentDoc = this.callbacks.getCurrentDoc();
    if (!currentDoc) return;
    const visibleFields = Array.from(this.docInfoFields);
    const customFields = { ...(currentDoc.meta.customFields || {}), _visibleFields: visibleFields };
    const response = await api.updateDocument(currentDoc.meta.id, { customFields });
    if (response.success && response.data) {
      this.callbacks.setCurrentDoc(response.data);
      storage.setLocalVersion(currentDoc.meta.id, response.data.meta.version);
    }
  }

  loadVisibleFields(): void {
    const currentDoc = this.callbacks.getCurrentDoc();
    if (!currentDoc) return;
    const customFields = currentDoc.meta.customFields || {};
    const saved = (customFields as any)._visibleFields as string[] | undefined;
    if (saved && Array.isArray(saved)) {
      this.docInfoFields = new Set(saved);
      if (currentDoc.meta.tags && currentDoc.meta.tags.length > 0) {
        this.docInfoFields.add('tags');
      }
    } else {
      const autoFields = new Set<string>();

      const customFieldKeys = ['summary', 'speakers', 'author', 'platform', 'url', 'area', 'outline'];
      for (const key of customFieldKeys) {
        const value = (customFields as any)[key];
        if (value && typeof value === 'string' && value.trim()) {
          autoFields.add(key);
        }
      }

      if (currentDoc.meta.resourceCategory) {
        autoFields.add('resourceCategory');
      }
      if (currentDoc.meta.tags && currentDoc.meta.tags.length > 0) {
        autoFields.add('tags');
      }
      if (currentDoc.meta.project) {
        autoFields.add('project');
      }
      if (currentDoc.meta.created_at) {
        autoFields.add('date');
      }

      if (autoFields.size === 0) {
        autoFields.add('summary');
      }
      this.docInfoFields = autoFields;
    }
  }

  private getCustomSelectOptions(fieldType: string): string[] {
    try {
      return JSON.parse(localStorage.getItem(`field_options_${fieldType}`) || '[]');
    } catch {
      return [];
    }
  }

  private addCustomSelectOption(fieldType: string, value: string): void {
    const options = this.getCustomSelectOptions(fieldType);
    if (!options.includes(value)) {
      options.push(value);
      localStorage.setItem(`field_options_${fieldType}`, JSON.stringify(options));
    }
  }
}
